import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import { isEmpty, keys, pickBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { ResourceActiveDisabled, ResourceActiveEnabled } from '~/src/dash/api/console-api';
import { SimpleMultiSelect } from '~/src/dash/components/dropdowns/SimpleMultiSelect';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ModelDialogDeleteView } from '~/src/dash/components/model-dialog-delete-view';
import { extractSortsFromColumns } from '~/src/dash/components/sorts/common';
import { SortByRow } from '~/src/dash/components/sorts/SortByRow';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  IListViewTableColumnInfo,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { IconTextToggle } from '~/src/dash/components/toggles/IconTextToggle';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TextInput,
  useIsMounted,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  navigateToBulkUpdateRoute,
  RouteChannelBulkUpdate,
  RouteChannelExisting,
} from '~/src/dash/router/routes';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import { muiColors, publicaColors } from '~/src/dash/utils/colors';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IChannelRow, IChannelsUiParams } from '~/src/dash/views/channels/api';
import { formatDataToLVTRows, IChannelsConfig } from '~/src/dash/views/channels/Channels/config';
import { useChannelsStore } from '~/src/dash/views/channels/contexts';

const BULK_ACTION_COLOR = publicaColors.blue;

interface IChannelListProps {
  channels: IChannelRow[];
  fetchChannels: (
    params: IChannelsUiParams,
    selectedFilters: Record<string, Set<string | number>>,
  ) => void;
  updateChannels: (
    channelIDs: string[],
    updates: Partial<IChannelRow>,
    selectedFilters: Record<string, Set<string | number>>,
  ) => void;
  cfg: IChannelsConfig;
  updateCfg: (params: IChannelsUiParams) => void;
  deleteChannels: (
    channelIDs: string[],
    selectedFilters: Record<string, Set<string | number>>,
  ) => Promise<void>;
  navigateToNewChannelView: () => void;
  totalRows: number;
  isActionPending: boolean;
  loadState: LoadingState;
}

export const Channels = ({
  channels,
  fetchChannels,
  deleteChannels,
  updateChannels,
  cfg,
  updateCfg,
  navigateToNewChannelView,
  isActionPending,
  totalRows,
  loadState,
}: IChannelListProps) => {
  const { t } = usePublicaTranslation();
  const { session, user } = useBaseStore();
  const { readAuthorization, writeAuthorization } = useChannelsStore();
  const { cypressClassName, defaultState } = cfg;
  const isSuperUser = hasRoleSuperUser(user.user);
  const isMounted = useIsMounted();

  const [order, setOrder] = useState(defaultState.orderDirection);
  const [orderBy, setOrderBy] = useState(defaultState.orderBy);
  const [searchInput, setSearchInput] = useState('');
  const [currPage, setCurrPage] = useState(defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(defaultState.rowsPerPage);

  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [activeColumns, setActiveColumns] = useState<IMenuList>(
    extractVisibleColumns(cfg.table.columns, defaultState.activeColumnKeys || [], isSuperUser),
  );
  const [selectedChannels, setSelectedChannels] = useState<string[]>([]);
  const [deleteModalActive, setDeleteModalActive] = useState(false);

  // search box
  const handleUpdateSearchInput = (text: string) => {
    track('Channel List - Search', { isSuperUser }, user.user);
    setSearchInput(text);
  };

  // filter functions
  const handleFilterAccept = (value: Set<number | string>, key: string): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      setSelectedFilters(clonedSelectedFilters);
      track('Channel List - Use Filter', { isSuperUser, filter: key }, user.user);
    }
  };

  // clear all filters
  const handleClearFilters = () => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      clearedSelectedFilters[filter.key] = new Set<string | number>(
        filter.selectedOptionIDsOnReset || [],
      );
    });
    setSelectedFilters(clearedSelectedFilters);
  };

  const populateFilters = (): void => {
    setFiltersLoadState(LoadingStateLoading);
    const keys: string[] = [];
    const getListFns: Array<Promise<ISelectOption[]>> = [];
    Object.values(cfg.filters).forEach((filter) => {
      keys.push(filter.key);
      getListFns.push(filter.getList(session, user));
    });

    const tempFilters: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};

    Promise.all(getListFns)
      .then((results) => {
        results.forEach((result, index) => {
          const filterKey = keys[index];
          tempFilters[filterKey] = result;
          tempSelectedFilters[filterKey] =
            cfg.filters[filterKey].selectedOptionIDs || new Set<string | number>();
        });
        unstable_batchedUpdates(() => {
          setFilters(tempFilters);
          setFiltersLoadState(LoadingStateHasData);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setFilters({});
          setSelectedFilters({});
          setFiltersLoadState(LoadingStateNoData);
        });
      });
  };

  // get url string from a channel id
  const getChannelRoute = (id: string): string => {
    return getRoutePath(RouteChannelExisting, extractRouteParams({ session, user }), { ch: id });
  };

  // update sort filter when click on an option from the filter bar
  const handleSelectSortBy = (orderByKey: string) => {
    track('Channel List - Sort Row', { isSuperUser, orderByKey }, user.user);
    unstable_batchedUpdates(() => {
      setSelectedChannels([]);
      setOrder(cfg.table.columns[orderByKey].defaultOrderDirection || 'asc');
      setOrderBy(cfg.table.columns[orderByKey].orderByKey || orderByKey);
      setCurrPage(0);
    });
  };

  // check if user select any of the trend columns
  const isTrendActive = () => {
    for (const key in activeColumns) {
      if (cfg.trends.includes(key) && activeColumns[key].selected) {
        return true;
      }
    }
    return false;
  };

  // update sort filter when click on a column header
  const handleClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection, label } = column;
    track('Channel List - Column Header Sort', { isSuperUser, sortBy: label }, user.user);
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setSelectedChannels([]);
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setSelectedChannels([]);
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setCurrPage(0);
      });
    }
  };

  // update active columns of the table
  const handleChangeActiveColumns = (keys: string[]) => {
    track('Channel List - Select Active Columns', { isSuperUser, activeColumns: keys }, user.user);
    const updatedActiveColumns = {} as IMenuList;
    for (const key in activeColumns) {
      if (activeColumns.hasOwnProperty(key)) {
        updatedActiveColumns[key] = { ...activeColumns[key], selected: keys.includes(key) };
      }
    }
    updateCfg({ activeColumnKeys: keys });
    setActiveColumns(updatedActiveColumns);
  };

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    track(
      'Channel List - Change Rows Per Page',
      { isSuperUser, rowsPerPage: updatedRowsPerPage },
      user.user,
    );
    unstable_batchedUpdates(() => {
      setSelectedChannels([]);
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  // update status of selected channel(s)
  const handleChangeStatus = (id: string, checked: boolean) => {
    const value: number = checked ? ResourceActiveEnabled : ResourceActiveDisabled;
    updateChannels([id], { Active: value }, selectedFilters);
  };

  // update the list of selected channels
  const handleChangeSelectedChannels = (id: string) => {
    const selectedIndex = selectedChannels.indexOf(id);
    let selected: string[] = [];
    if (selectedIndex === -1) {
      selected = selected.concat(selectedChannels, id);
    } else if (selectedIndex === 0) {
      selected = selected.concat(selectedChannels.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      selected = selected.concat(selectedChannels.slice(0, -1));
    } else if (selectedIndex > 0) {
      selected = selected.concat(
        selectedChannels.slice(0, selectedIndex),
        selectedChannels.slice(selectedIndex + 1),
      );
    }
    setSelectedChannels(selected);
  };

  // click on the Trends button
  const handleOnClickShowTrends = () => {
    const currActiveKeys = keys(pickBy(activeColumns, (column: IListItem) => column.selected));
    let updatedActiveKeys: string[];
    if (isTrendActive()) {
      updatedActiveKeys = currActiveKeys.filter((key) => !cfg.trends.includes(key));
    } else {
      track('Channel List - Show Trends', { isSuperUser }, user.user);
      updatedActiveKeys = currActiveKeys;
      cfg.trends.forEach((trend) => {
        const associatedKey = cfg.table.columns[trend].parentKey;
        const trendIdx = updatedActiveKeys.indexOf(trend);
        if (trendIdx === -1) {
          if (associatedKey && !updatedActiveKeys.includes(associatedKey)) {
            updatedActiveKeys.push(associatedKey);
          }
          updatedActiveKeys.push(trend);
        } else {
          if (associatedKey && updatedActiveKeys.includes(associatedKey)) {
            updatedActiveKeys.push(associatedKey);
          }
        }
      });
    }

    setActiveColumns(
      extractVisibleColumns(cfg.table.columns, updatedActiveKeys || [], isSuperUser),
    );
    updateCfg({ activeColumnKeys: updatedActiveKeys });
  };

  // update to select all channels on the current page
  const handleSelectAllChannels = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedChannels(channels.map((channel) => String(channel.ID)));
    } else {
      setSelectedChannels([]);
    }
  };

  useEffect(() => {
    populateFilters();
  }, []);

  useEffect(() => {
    if (!isEmpty(selectedFilters)) {
      Object.keys(selectedFilters).forEach((key) => {
        cfg.filters[key].selectedOptionIDs = selectedFilters[key];
      });
    }
  }, [selectedFilters]);

  // refetch channels when any of the filter condition changes
  useEffect(() => {
    if (!isMounted()) {
      track('Channel List - View', { isSuperUser }, user.user);
    }
    fetchChannels(
      {
        orderBy,
        order,
        searchInput,
        rowsPerPage,
        currPage,
      },
      selectedFilters,
    );
  }, [searchInput, currPage, rowsPerPage, order, orderBy, selectedFilters]);

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
    >
      {/* NEW CHANNEL */}
      <Panel style={{ textAlign: 'right' }}>
        <ButtonV2
          onClick={navigateToNewChannelView}
          className={`cypress-${cypressClassName}-button-createsegment`}
          disabled={!writeAuthorization}
          tKey={'CreateItem'}
          tProps={{ item: t('Channel') }}
        />
      </Panel>

      {/* FILTER & SORT */}
      <Panel highlighted>
        <Rows>
          <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
            <FilterRowV2
              cypressParentName={cypressClassName}
              filtersToOptionsMap={filters}
              filtersConfig={cfg.filters}
              handleApplySelect={handleFilterAccept}
              handleClearFilters={handleClearFilters}
              enableClear={true}
              selectedFilters={selectedFilters}
              loadState={filtersLoadState}
            />
          </Row>
          <Row noPadding>
            <SortByRow
              sorts={extractSortsFromColumns(cfg.sorts, cfg.table.columns)}
              currentOrderBy={orderBy}
              currentOrder={order}
              handleSelectSortBy={handleSelectSortBy}
            />
          </Row>
        </Rows>
      </Panel>

      {/* SEARCH & ACTIVE COLUMNS */}
      <Panel highlighted largeHorizontalPadding verticalPadding>
        <Rows>
          <Row style={{ padding: '8px 0px' }}>
            <Cols gap={spacing.small} style={{ alignItems: 'center' }}>
              <Col>
                <TextInput
                  enableClear
                  handleOnChange={handleUpdateSearchInput}
                  msDelay={800}
                  placeholderTKey='SearchByNameID'
                  prefix='search'
                  initialValue={searchInput}
                />
              </Col>
              <Col>
                <SearchResultsText numRowsDisplayed={channels.length} numTotalRows={totalRows} />
              </Col>
              <Col style={{ marginLeft: 'auto' }}>
                <IconTextToggle
                  buttonStyle={{ padding: '5px 12px 4px' }}
                  handleOnClick={handleOnClickShowTrends}
                  isActive={isTrendActive()}
                  iconSlug='trends'
                  tKey='Trends'
                  tooltip={t('Trends')}
                />
              </Col>
              <Col>
                <SimpleMultiSelect
                  buttonStyle={{ padding: '2px 4px 2px 12px' }}
                  resetKeys={cfg.columnSelector.resetKeys}
                  preselectedKeys={defaultState.activeColumnKeys}
                  label={t('ActiveColumns')}
                  list={activeColumns}
                  handleOnChangeOption={handleChangeActiveColumns}
                  handleOnAccept={handleChangeActiveColumns}
                  singularLabel='Active Column'
                  iconPath='/assets/shared/svg/ColumnEdit.svg'
                />
              </Col>
            </Cols>
          </Row>

          {/* BULK UPDATE & DELETE */}
          {selectedChannels.length > 1 && (
            <Row style={{ padding: '8px 0 0 0' }}>
              <Cols
                wrap
                style={{
                  alignItems: 'center',
                  rowGap: spacing.extraSmall,
                }}
              >
                <Col>
                  <Text
                    small
                    bold
                    color={muiColors.checkbox}
                    tKey='NumItemsSelected'
                    tProps={{
                      count: selectedChannels.length,
                      item: t('Channels'),
                    }}
                  />
                </Col>
                <Col paddingLeft='1em'>
                  <TextButton
                    allCaps
                    onClick={() =>
                      navigateToBulkUpdateRoute(
                        RouteChannelBulkUpdate,
                        selectedChannels,
                        extractRouteParams({ session, user }),
                      )
                    }
                  >
                    <Cols style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <EditIcon style={{ height: '0.65em', color: BULK_ACTION_COLOR }} />
                      </Col>
                      <Col>
                        <Text small bold color={BULK_ACTION_COLOR} tKey='BulkUpdate' />
                      </Col>
                    </Cols>
                  </TextButton>
                </Col>
                <Col paddingLeft='1em'>
                  <TextButton allCaps onClick={() => setDeleteModalActive(true)}>
                    <Cols style={{ alignItems: 'center' }}>
                      <Col displayFlex>
                        <DeleteIcon style={{ height: '0.65em', color: BULK_ACTION_COLOR }} />
                      </Col>
                      <Col>
                        <Text small bold color={BULK_ACTION_COLOR} tKey='Delete' />
                      </Col>
                    </Cols>
                  </TextButton>
                </Col>
              </Cols>
            </Row>
          )}

          {/* TABLE */}
          <Row noHorizontalPadding>
            <ListViewTable
              activeColumnKeys={Object.values(activeColumns)
                .filter((column) => column.selected)
                .map((column) => column.ID)}
              columns={cfg.table.columns}
              displayNameWithID={!isDemoAccount(user.user.Username)}
              enableActions
              enableSelectCheckbox
              enableStatusSwitch
              getItemSettingsRoute={getChannelRoute}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              handleDeleteItem={(id: string) => deleteChannels([id], selectedFilters)}
              handleOnChangeStatus={handleChangeStatus}
              handleOnChangeSelect={handleChangeSelectedChannels}
              handleOnClickColumnHeader={handleClickColumnHeader}
              handleSelectAllClick={handleSelectAllChannels}
              isActionPending={isActionPending}
              isReadAuthorized={readAuthorization.isAuthorized}
              isSuperUser={isSuperUser}
              isWriteAuthorized={writeAuthorization.isAuthorized}
              loadState={loadState}
              order={order}
              orderBy={orderBy}
              page={currPage}
              rows={formatDataToLVTRows(channels, cfg.table)}
              rowsPerPage={rowsPerPage}
              selected={selectedChannels}
              totalRowCount={totalRows}
              className={cypressClassName}
            />
          </Row>
        </Rows>
      </Panel>

      {/* BULK DELETE MODAL */}
      <ModelDialogDeleteView
        disableSnackbar
        active={deleteModalActive}
        model={t('Channels')}
        Name={
          selectedChannels.length > 1
            ? t('FormatNumItems', { count: selectedChannels.length, item: t('Channels') })
            : selectedChannels[0]
        }
        ID={
          selectedChannels.length > 1 ? `${selectedChannels.length} Channels` : selectedChannels[0]
        }
        deleteModel={() => deleteChannels(selectedChannels, selectedFilters)}
        onDelete={() => {
          unstable_batchedUpdates(() => {
            setDeleteModalActive(false);
            setSelectedChannels([]);
          });
        }}
        onError={() => setDeleteModalActive(false)}
        onClose={() => setDeleteModalActive(false)}
      />
    </Panels>
  );
};
